import type { View, ViewContext } from '../router';
import { el, ensureStyle } from '../dom';
import { activePacks } from '../../core/region';
import {
  licenseUrl,
  loadAllAttributions,
  sourceLabel,
  summarize,
  type CreditsSummary,
} from '../../core/attributions';
import { icon } from '../components/icons';
import { t, tParts } from '../../strings';
import { listOf } from '../../strings/format';

// The credits roll. Every recording and photo in the app is a Creative Commons
// work, and CC-BY and its descendants all require attribution the user can
// actually see — species pages carry the per-asset credit, and this is the
// whole-region version, plus the license breakdown for anyone auditing it.

ensureStyle(
  'credits-css',
  `
  .credits { padding:18px var(--pad); display:flex; flex-direction:column; gap:18px; }
  .credits .back { align-self:flex-start; margin-left:-6px; }
  .credits h1 { font-size:30px; margin:0; }
  .credits h2 { font-size:17px; margin:0 0 10px; }
  .credits .intro { line-height:1.55; }
  .credits .tally { display:flex; flex-wrap:wrap; gap:7px; }
  /* Licence counts as chips: four short facts that would read as a wall of
     commas in a sentence. */
  .credits .tally .chip { font-size:12px; padding:5px 11px; gap:5px; }
  .credits .tally .chip b { font-weight:600; }
  .credits .tally a { text-decoration:none; color:inherit; }
  .credits .roll { display:flex; flex-direction:column; }
  .credits .who { display:flex; align-items:baseline; gap:10px; padding:8px 2px;
    border-bottom:1.5px solid var(--hairline); }
  .credits .who:last-child { border-bottom:none; }
  .credits .who .name { flex:1; min-width:0; font-family:var(--font-display);
    font-size:14.5px; color:var(--ink); text-decoration:none;
    overflow-wrap:anywhere; }
  .credits .who .n { flex:none; text-align:right; }
  .credits .sources { display:flex; flex-direction:column; gap:6px; }
  .credits .sources a { color:var(--ink); }
  .credits .status { color:var(--ink-soft); font-style:italic; }
`,
);

/** "Pacific Northwest", or "Pacific Northwest and Cape Town". */
function packNames(): string {
  const labels = activePacks().map((m) => m.label);
  return labels.length ? listOf(labels) : t('you.credits.thisPack');
}

export const creditsView: View = (root: HTMLElement, ctx: ViewContext) => {
  const body = el('div.status.meta', {}, t('you.credits.loading'));

  root.append(
    el(
      'div.credits',
      {},
      el(
        'button.back.btn.btn-plain',
        { onclick: () => ctx.navigate('/you'), 'aria-label': t('you.credits.back') },
        icon('arrow-left', { size: 22 }),
      ),
      el('h1', {}, t('you.credits.title')),
      el(
        'div.intro.meta',
        {},
        // Names the packs actually credited below, which under "All packs" is
        // more than one — the primary manifest's label alone would put a Cape
        // Town recordist's name under a Pacific Northwest heading.
        t('you.credits.intro', { packs: packNames() }),
      ),
      body,
    ),
  );

  // Fetched after the shell paints — the list is ~170 KB and grows with the
  // species count, so it is deliberately not part of the app bundle.
  // Every selected pack's credits, not just the primary one's — a learner
  // studying all packs is being shown media from all of them, so all of their
  // authors have to appear.
  void loadAllAttributions(activePacks().map((m) => m.id)).then(
    (entries) => body.replaceWith(...sections(summarize(entries))),
    (err: Error) => {
      body.classList.add('status');
      body.textContent = err.message;
    },
  );
};

function sections(s: CreditsSummary): HTMLElement[] {
  return [
    el(
      'section.card',
      {},
      el('h2', {}, t('you.credits.contents')),
      el(
        'div.sources',
        {},
        el(
          'div.meta',
          {},
          ...tParts('you.credits.sources', {
            audio: s.audio,
            xenoCanto: link('https://xeno-canto.org', sourceLabel('xeno-canto')),
            photos: s.photos,
            iNaturalist: link('https://www.inaturalist.org', sourceLabel('inaturalist')),
          }),
        ),
        el('div.tally', {}, ...s.licenses.map(licenseChip)),
      ),
    ),
    el(
      'section',
      {},
      el('h2', {}, t('you.credits.contributors', { n: s.contributors.length })),
      el(
        'div.roll',
        {},
        ...s.contributors.map((c) =>
          el(
            'div.who',
            {},
            link(c.sourceUrl, c.author, 'name'),
            el('span.n.note', {}, assetCount(c.audio, c.photos)),
          ),
        ),
      ),
    ),
  ];
}

function licenseChip({ license, count }: CreditsSummary['licenses'][number]): HTMLElement {
  return el(
    'span.chip',
    {},
    link(licenseUrl(license), license),
    el('b', {}, String(count)),
  );
}

/** "3 recordings · 1 photo", with the zero side omitted rather than shown. */
function assetCount(audio: number, photos: number): string {
  if (audio && photos) return t('you.credits.assetsBoth', { audio, photos });
  if (audio) return t('you.credits.assetsAudio', { audio });
  if (photos) return t('you.credits.assetsPhotos', { photos });
  return '';
}

/** External link — every destination here is off-site. */
function link(href: string, text: string, cls = ''): HTMLElement {
  return el(`a${cls ? '.' + cls : ''}`, {
    href,
    target: '_blank',
    rel: 'noopener noreferrer',
  }, text);
}
